<!--
CercleDigital InsiderGems, gestion électronique documentaire partagée de l'écosystème Cercle.
Copyright 2026 Pierre-Thibault Fabre <pierre-thibault@cercle.digital>
SPDX-License-Identifier: LicenseRef-Cercle-Interne

Fichier : apps/web/src/shell/EnteteApplication.vue
Rôle : barre d'en-tête fixe, à la couleur de contraste primaire.
Référence : designsystem.md ; CIG-SPECIFICATION.md §5.4.
-->
<script setup lang="ts">
import LogoCercle from '../assets/LogoCercle.vue';

defineProps<{ lateraleOuverte: boolean }>();
const emit = defineEmits<{ basculerLaterale: [] }>();
</script>

<template>
  <header class="entete">
    <button
      class="bascule"
      :aria-expanded="lateraleOuverte"
      aria-controls="navigation-laterale"
      aria-label="Afficher ou masquer la navigation"
      @click="emit('basculerLaterale')"
    >
      <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
        <path d="M4 7h16M4 12h16M4 17h16" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" fill="none" />
      </svg>
    </button>

    <RouterLink to="/" class="marque" aria-label="Cercle Gems, accueil">
      <!-- Le lettrage hérite de la couleur du texte : blanc sur l'en-tête. -->
      <LogoCercle class="logo" aria-hidden="true" />
      <span class="titre">Cercle Gems</span>
    </RouterLink>

    <div class="fin">
      <slot name="fin" />
    </div>
  </header>
</template>

<style scoped>
/*
 * Barre fixe, à la couleur de contraste primaire du design system. Elle est
 * la seule surface sombre de l'interface, dont le thème clair est le défaut.
 */
.entete {
  position: fixed;
  inset: 0 0 auto 0;
  z-index: 40;
  height: var(--cig-entete);
  display: flex;
  align-items: center;
  gap: var(--cig-espace-3);
  padding: 0 var(--cig-espace-3);
  background: var(--cig-contraste);
  color: var(--cig-contraste-encre);
  /* Ombre noire faiblement transparente : la barre se détache du blanc de
     l'explorateur sans introduire de couleur. */
  box-shadow: 0 2px 10px rgb(0 0 0 / 14%);
}

.bascule {
  display: grid;
  place-items: center;
  width: var(--cig-touche);
  height: var(--cig-touche);
  padding: 0;
  min-height: 0;
  background: transparent;
  color: inherit;
  border-radius: var(--cig-rayon-petit);
  flex: none;
}
.bascule:hover { background: rgb(255 255 255 / 10%); opacity: 1; }

.marque {
  display: flex;
  align-items: center;
  gap: var(--cig-espace-2);
  color: inherit;
  text-decoration: none;
  min-width: 0;
}

/* Le lettrage fait 2004×540 : sa hauteur commande, la largeur suit. */
.logo { height: 22px; width: auto; flex: none; }

.titre {
  font-family: var(--cig-sans);
  font-size: 1rem;
  font-weight: 600;
  letter-spacing: -0.01em;
  white-space: nowrap;
  /* Filet de séparation discret, à la place d'une bordure franche. */
  padding-left: var(--cig-espace-2);
  border-left: 1px solid rgb(255 255 255 / 22%);
}

.fin { margin-left: auto; display: flex; align-items: center; gap: var(--cig-espace-1); }

@media (max-width: 40rem) {
  .entete { gap: var(--cig-espace-2); padding: 0 var(--cig-espace-2); }
  .logo { height: 18px; }
  .titre { font-size: 0.92rem; }
}

@media (max-width: 26rem) {
  .titre { display: none; }
}
</style>
